/* eslint-disable react/no-unescaped-entities */
'use client';

import { useMemo } from 'react';
import { Tabs, Heading, TAB_DATA, type TabData } from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface TabsDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

function fallbackTabs(prefix: string): TabData[] {
  return TAB_DATA.map((t) => ({
    ...t,
    id: `${prefix}-${t.id}`,
    panelId: `${prefix}-${t.panelId}`,
  }));
}

const TabsDemo: React.FC<TabsDemoProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const tabsStandard = useMemo(() => fallbackTabs('tab1'), []);
  const tabsPills = useMemo(() => fallbackTabs('tab2'), []);
  const tabsRounded = useMemo(() => fallbackTabs('tab3'), []);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Tabs', 'Pestañas')}</Heading>
        <p className='sub full'>
          {t(
            'Accessible tab interface with ARIA roles, keyboard navigation (←/→, Home/End), focus management, and horizontal scrolling for overflow. Only one tab panel is visible at a time, and all controls are screen reader friendly.',
            'Interfaz de pestañas accesible con roles ARIA, navegación de teclado (←/→, Inicio/Fin), gestión del foco y desplazamiento horizontal para el exceso. Solo se muestra un panel de pestaña a la vez, y todos los controles son compatibles con lectores de pantalla.'
          )}
        </p>
        <Tabs tabs={tabsStandard} id='tab1' />
        <p className='mt-32'>{t('Pill style tabs.', 'Pestañas con estilo de píldoras.')}</p>
        <Tabs tabs={tabsPills} id='tab2' className='tabs-pills' />
        <p className='mt-32'>
          {t(
            'Rounded tabs with a flat bottom that sits on the panel.',
            'Pestañas redondeadas con la base plana sobre el panel.'
          )}
        </p>
        <Tabs tabs={tabsRounded} id='tab3' variant='rounded' />
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="tabs" data-tabs>
  <div class="tabs-desktop">
    <div class="tablist scrollable" role="tablist">
      <button class="tab is-active" type="button" role="tab" aria-selected="true">Overview</button>
      <button class="tab" type="button" role="tab">Details</button>
    </div>
    <div class="card tab-panels">
      <div class="panel" role="tabpanel">Panel content</div>
    </div>
  </div>
</div>

<div class="tabs tabs-pills" data-tabs></div>
<div class="tabs tabs-rounded" data-tabs></div>`}
        />
      </Container>
    </Section>
  );
};

export default TabsDemo;
